import React, { useState } from 'react';

// The four things a person can do about one "Needs you today" row: complete it, snooze it to a day,
// add a note to its deal, or clear it. Writes go to routes/queue.js (and the deal's own notes); the
// Dashboard re-reads afterwards through onDone, so the row disappears because the SERVER says it is
// done, not because this component decided so.
//
// What each does is the person's own: another member's queue never changes.
//   Complete — hides it and writes a line to the deal's activity timeline.
//   Clear    — hides it. On a single-deal row it asks first: just this one, or every notification
//              showing for that deal (and its open follow-up reminders)?
//   Snooze   — hides it until a day, in the market's timezone.
//   Note     — a deal note (the same notes the deal page shows); the row then shows the latest one.

// 'YYYY-MM-DD' for today in the market's timezone, and n days on. Built from parts, never through
// new Date('YYYY-MM-DD'), which is UTC midnight and a day early anywhere in the US.
function marketToday(tz) {
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'America/Chicago' }).format(new Date()); }
  catch { return new Intl.DateTimeFormat('en-CA').format(new Date()); }
}
function addDays(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}
function nextMonday(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();   // 0 = Sunday
  return addDays(ymd, ((8 - dow) % 7) || 7);
}

async function post(url, body) {
  const r = await fetch(url, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Something went wrong');
  return d;
}

export default function QueueItemActions({ item, queue, tz, onDone }) {
  const [open, setOpen] = useState(null);          // 'snooze' | 'note' | 'clear' | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [day, setDay] = useState('');
  const [note, setNote] = useState('');

  const keys = (item.keys || []).map((k) => k.key);
  if (!keys.length) return null;
  const single = item.deal_id != null && (item.keys || []).length === 1;
  const today = marketToday(tz);

  // Every key on the board that is about this row's deal — including grouped rows that list it.
  const dealKeys = single
    ? (queue || []).flatMap((q) => (q.keys || []).filter((k) => k.deal_id === item.deal_id).map((k) => k.key))
    : [];
  const otherCount = new Set(dealKeys).size - 1;

  // `done` = the keys this action puts away, so the Dashboard can drop them at once (a note puts none away).
  const run = async (fn, done = []) => {
    setBusy(true); setError(null);
    try { await fn(); setOpen(null); setNote(''); setDay(''); onDone && onDone(done); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const act = (action, extra = {}) => run(() => post('/api/queue/actions', { action, keys, title: item.title, ...extra }), keys);
  const clearAll = () => run(() => post(`/api/queue/deals/${item.deal_id}/clear-all`, { keys: [...new Set(dealKeys)] }), dealKeys);
  const addNote = () => run(() => post(`/api/deals/${item.deal_id}/notes`, { body: note }));

  const picks = [['Tomorrow', addDays(today, 1)], ['In 3 days', addDays(today, 3)], ['Next week', nextMonday(today)]];

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        <button disabled={busy} onClick={() => act('complete')} style={btn} title="Mark complete — logged on the deal's timeline">✓ Complete</button>
        <button disabled={busy} onClick={() => setOpen(open === 'snooze' ? null : 'snooze')} style={btn} title="Remind me on a later day">Snooze</button>
        {item.deal_id != null && (
          <button disabled={busy} onClick={() => setOpen(open === 'note' ? null : 'note')} style={btn} title="Add a note to this deal">Note</button>
        )}
        <button disabled={busy} onClick={() => setOpen(open === 'clear' ? null : 'clear')} style={btn} title="Clear this notification">Clear</button>
      </div>

      {open && (
        <div style={pop} onClick={(e) => e.stopPropagation()}>
          {open === 'snooze' && (
            <>
              <div style={popTitle}>Remind me on…</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {picks.map(([label, d]) => (
                  <button key={label} disabled={busy} onClick={() => act('snooze', { snooze_until: d })} style={btn}>{label}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="date" min={addDays(today, 1)} value={day} onChange={(e) => setDay(e.target.value)} style={input} />
                <button disabled={busy || !day} onClick={() => act('snooze', { snooze_until: day })} style={{ ...btn, ...primary }}>Snooze</button>
              </div>
            </>
          )}

          {open === 'note' && (
            <>
              <div style={popTitle}>Note on {item.address || 'this deal'}</div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus
                placeholder="Saved to the deal's notes" style={{ ...input, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                <button disabled={busy || !note.trim()} onClick={addNote} style={{ ...btn, ...primary }}>Save note</button>
              </div>
            </>
          )}

          {open === 'clear' && (
            single ? (
              <>
                <div style={popTitle}>Clear notification</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 10, lineHeight: 1.45 }}>
                  Clear just this one, or every follow-up notification for {item.address || 'this deal'}?
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button disabled={busy} onClick={() => act('clear')} style={{ ...btn, ...wrap }}>Clear just this notification</button>
                  <button disabled={busy} onClick={clearAll} style={{ ...btn, ...danger, ...wrap }}>
                    Delete all follow-up notifications for this deal{otherCount > 0 ? ` (${otherCount + 1} here + reminders)` : ' (incl. reminders)'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={popTitle}>Clear {item.count > 1 ? `these ${item.count}` : 'this'}?</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                  <button onClick={() => setOpen(null)} style={btn}>Cancel</button>
                  <button disabled={busy} onClick={() => act('clear')} style={{ ...btn, ...danger }}>Clear</button>
                </div>
              </>
            )
          )}
          {error && <div style={{ fontSize: 11.5, color: 'var(--red, #c0392b)', marginTop: 8 }}>{error}</div>}
        </div>
      )}
    </div>
  );
}

const btn = { fontSize: 11, fontWeight: 600, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', borderRadius: 7, padding: '3px 8px', fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' };
const primary = { background: 'var(--teal)', borderColor: 'var(--teal)', color: '#fff' };
const wrap = { whiteSpace: 'normal', textAlign: 'left', padding: '6px 9px', lineHeight: 1.35 };
const danger = { borderColor: 'rgba(192,57,43,0.5)', color: 'var(--red, #c0392b)' };
const input = { fontSize: 12.5, padding: '5px 8px', borderRadius: 7, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', boxSizing: 'border-box' };
const pop = { position: 'absolute', left: 0, top: 'calc(100% + 6px)', zIndex: 20, width: 280, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.18)' };
const popTitle = { fontSize: 12, fontWeight: 700, marginBottom: 8 };
